import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { SessionRepository } from '../repositories/sessionRepository';
import type {
	ActivateUserTemporaryPassword,
	ForgotPassword,
	Login,
	MFA_CODE,
	ResetPassword,
} from '../repositories/sessionRepository';

export const sessionKeys = {
	getStatus: () => ['status'] as const,
	getSaml: (email: string) => ['saml', email] as const,
	getActivateUser: (userId: string, activationToken: string) =>
		['userId', userId, 'activationToken', activationToken] as const,
	confirmToken: (activationToken: string, userId: string) =>
		['activationToken', activationToken, 'userId', userId] as const,
};

export const useSessionLogoutQuery = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: SessionRepository.logout,
		onSettled: () => {
			queryClient.setDefaultOptions({
				queries: {
					enabled: false,
				},
			});
			queryClient.clear();
			queryClient.removeQueries();
		},
	});
};

export const authStatusQuery = queryOptions({
	queryKey: sessionKeys.getStatus(),
	queryFn: SessionRepository.getStatus,
	staleTime: 0,
	retry: false,
});

export const useLoginQuery = <T>() => {
	return useMutation({
		mutationFn: (data: Login) => SessionRepository.login<T>(data),
	});
};

export const useForgotPasswordQuery = () => {
	return useMutation({
		mutationFn: (data: ForgotPassword) => SessionRepository.forgotPassword(data),
	});
};

export const useResetPasswordQuery = () => {
	return useMutation({
		mutationFn: (data: ResetPassword) => SessionRepository.resetPassword(data),
	});
};

export const getSamlQuery = (email: string) => ({
	queryKey: [sessionKeys.getSaml(email)],
	queryFn: () => SessionRepository.getSaml(email),
});

export const useRegisterQuery = <Args, Response>() => {
	return useMutation({
		mutationFn: (data: Args) => SessionRepository.register<Args, Response>(data),
	});
};

export const useActivateUserQuery = <ArgumentsType, ResponseData>() =>
	useMutation<ResponseData, AxiosError, ArgumentsType>({
		mutationFn: (options: ArgumentsType) => SessionRepository.activateUser<ArgumentsType, ResponseData>(options),
	});

export const useSendMfaCodeQuery = () => {
	return useMutation({
		mutationFn: (data: MFA_CODE) => SessionRepository.sendMfaCode(data),
	});
};

export const useAskForSmsCodeQuery = () => {
	return useMutation({
		mutationFn: SessionRepository.askForSmsCode,
	});
};

export const useSendSmsCodeQuery = () => {
	return useMutation({
		mutationFn: (data: MFA_CODE) => SessionRepository.sendSmsCode(data),
	});
};

export const useSendMfaCodeChallengeQuery = () => {
	return useMutation({
		mutationFn: (data: MFA_CODE) => SessionRepository.sendMfaCodeChallenge(data),
	});
};

export const useRequestActivationWithTemporaryPasswordQuery = () =>
	useMutation({
		mutationFn: (options: ActivateUserTemporaryPassword) =>
			SessionRepository.requestActivationWithTemporaryPasswordQuery(options),
	});

export const useReset2FAQuery = () => {
	return useMutation({
		mutationFn: (userId: string) => SessionRepository.reset2FA(userId),
	});
};
